import React, { useState, useEffect, useMemo } from "react";
import "./App.css";
import LandingIndex from "./componets/landingPage/LandingIndex";
import { AuthProvider } from "./contexts/AuthContext";
import { SupaProvider } from "./contexts/SupaContext";
import { SalesProvider } from "./contexts/SalesContext";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Dashboard from "./componets/dashboard/Dashboard";
import { Theme } from "@radix-ui/themes";
import { Toaster } from "react-hot-toast";
import PageNotFound from "./componets/navigation/PageNotFound";
import NewItems from "./componets/store/NewItems";
import ComingSoon from "./componets/pages/ComingSoon";
import AccessPage from "./componets/auth/AccessPage";
import PolicyTabs from "./componets/pages/PolicyTabs";
import PrivateRoute from "./routers/PrivateRoute";
import CoverageFullPage from "./componets/pages/CoverageFullPage";
import NewRefills from "./componets/store/NewRefills";
import OrderProcessing from "./componets/store/OrderProcessing";

// Componentes memoizados para optimización (estables)
const AuthProviderMemo = React.memo(AuthProvider);
const SupaProviderMemo = React.memo(SupaProvider);
const SalesProviderMemo = React.memo(SalesProvider);

// Wrapper para rutas que NECESITAN SalesContext
const WithSalesProvider = ({ children }) => {
  return <SalesProviderMemo>{children}</SalesProviderMemo>;
};

// Componente principal de rutas (optimizado)
const AppRoutes = React.memo(() => {
  return (
    <Routes>
      {/* Rutas Públicas - SIN SalesProvider */}
      <Route path="/" element={<LandingIndex />} />
      <Route path="/ingresar" element={<AccessPage />} />
      <Route path="/registro" element={<AccessPage initialTab="signup" />} />
      <Route
        path="/privacidad"
        element={<PolicyTabs activeSection="privacy" />}
      />
      <Route path="/datos" element={<PolicyTabs activeSection="data" />} />
      <Route path="/terminos" element={<PolicyTabs activeSection="terms" />} />
      <Route path="/manual" element={<PolicyTabs activeSection="manual" />} />
      <Route path="/docs" element={<PolicyTabs activeSection="terms" />} />
      <Route path="/cobertura" element={<CoverageFullPage />} />

      {/* Rutas Privadas - SIN SalesProvider */}
      <Route
        path="/app/*"
        element={
          <PrivateRoute>
            <Dashboard />
          </PrivateRoute>
        }
      />

      {/* Rutas que SÍ necesitan SalesProvider */}
      <Route
        path="/nuevo-pedido"
        element={
          <WithSalesProvider>
            <NewItems />
          </WithSalesProvider>
        }
      />
      <Route
        path="/recambios"
        element={
          <WithSalesProvider>
            <NewRefills />
          </WithSalesProvider>
        }
      />

      <Route
        path="/procesando-pedido"
        element={
          <WithSalesProvider>
            <OrderProcessing />
          </WithSalesProvider>
        }
      />
      {/* Catch-all para 404 */}
      <Route path="*" element={<PageNotFound />} />
    </Routes>
  );
});

AppRoutes.displayName = "AppRoutes";

// Componente separado para el contenido de la app (optimizado)
const AppContent = React.memo(() => {
  return (
    <SupaProviderMemo>
      <BrowserRouter>
        {/* Toaster para notificaciones */}
        <Toaster />
        <AppRoutes />
      </BrowserRouter>
    </SupaProviderMemo>
  );
});

AppContent.displayName = "AppContent";

// Hook personalizado para manejar el estado de la app (optimizado)
const useAppState = () => {
  const [isWebView, setIsWebView] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);

  // Estado para controlar si mostrar Coming Soon o la app
  const isComingSoon = false; // Cambia esto a false para mostrar la app completa

  useEffect(() => {
    const checkWebView = () => {
      const isStandalone = window.matchMedia(
        "(display-mode: standalone)"
      ).matches;
      const isIOSStandalone = window.navigator.standalone;
      setIsWebView(isStandalone || isIOSStandalone);
      setIsInitialized(true);
    };

    checkWebView();
  }, []);

  // Memoizar el resultado para evitar cambios innecesarios
  return useMemo(
    () => ({
      isWebView,
      isInitialized,
      isComingSoon,
    }),
    [isWebView, isInitialized, isComingSoon]
  );
};

// Componente de loading memoizado
const LoadingComponent = React.memo(() => (
  <div className="min-h-screen bg-gray-100 flex items-center justify-center">
    <div className="text-lg text-gray-600">Iniciando aplicación...</div>
  </div>
));

LoadingComponent.displayName = "LoadingComponent";

function App() {
  const { isWebView, isInitialized, isComingSoon } = useAppState();

  // Si isComingSoon es true, mostramos la página de Coming Soon
  if (isComingSoon) {
    return <ComingSoon />;
  }

  // Mostrar loading hasta que se inicialice
  if (!isInitialized) {
    return <LoadingComponent />;
  }

  // Aplicación normal optimizada
  return (
    <Theme panelBackground="solid" radius="large">
      <AuthProviderMemo>
        <AppContent />
      </AuthProviderMemo>
    </Theme>
  );
}

export default App;
